import { v4 as uuid } from 'uuid'
import conform from '~/lib/conformSettings'
import {
	ABLY_MESSAGES,
	EMPTY_ARRAY,
	EMPTY_SESSION,
	IS_DEV,
	RECORDING_STATUS,
	SESSION_STATUS,
	THUMBNAIL_MIME,
} from '~/lib/constants'
import { expectMedia } from '~/lib/ownMedia'
import { serverNow } from '~/lib/serverClock'
import socket from '~/lib/socket'
import { removePresetThumbnail, setThumbnail } from '~/routes/mobile/cache/presetThumbnailCache'
import { setProjectId } from '~/store/app'
import { selectProject, selectProjectId } from '~/store/app/selectors'
import { morphToPlain } from '~/lib/utils'
import { deleteObjects, setLiveProperty } from '~/store/live'
import { setCurrentPresetIndex } from '~/store/playback'
import { selectPresets } from '~/store/playback/selectors'
import { loadFirstTemplatePreset, loadPreset } from '~/store/playback/thunks'
import { setSelection } from '~/store/selection'
import { transformImage, transformProject } from '~/store/api/transforms'
import { parsePins } from '~/lib/pinLayer'
import { transformMap } from '~/lib/map'
import api, {
	ASSET,
	conformPresetSettings,
	CREDITS,
	GET_ACTIVE_SESSION,
	LIST_ASSETS,
	LIST_PROJECTS,
	LIST_RECORDINGS,
	LIST_SESSIONS,
	LIST_TEMPLATES,
	LIST_MAPS,
	PRESET,
	PROJECT,
	RECORDING,
	SESSION,
	TAGS,
	TEMPLATE,
	templateEndpointName,
	MAP,
} from './base'

const YEAR = 60 * 60 * 24 * 365

const logger = console.scope('api', true)

/** A map write's body: multipart when it carries a thumbnail Blob, else JSON. `data` undefined leaves it unchanged. */
const mapBody = ({ name, data, thumbnail }) => {
	const json = data === undefined ? undefined : JSON.stringify(data)
	if (!thumbnail) return { name, ...(json !== undefined && { data: json }) }
	const form = new FormData()
	if (name !== undefined) form.append('name', name)
	if (json !== undefined) form.append('data', json)
	form.append('thumbnail', thumbnail, `thumb.${THUMBNAIL_MIME.split('/')[1]}`)
	return form
}

api.injectEndpoints({
	endpoints: builder => ({
		// user
		getUser: builder.query({
			query: () => 'user',
			transformResponse: res => res.UserData,
		}),
		// Replaces the user's whole `settings` bag; the cache takes it optimistically.
		updateUserSettings: builder.mutation({
			query: settings => ({ url: 'user', method: 'PATCH', body: { settings } }),
			async onQueryStarted(settings, { dispatch, queryFulfilled }) {
				const patch = dispatch(
					api.util.updateQueryData('getUser', undefined, d => {
						d.settings = settings
					})
				)
				try {
					await queryFulfilled
				} catch (e) {
					logger.warn('updateUserSettings failed', e)
					patch.undo()
				}
			},
		}),
		// `{ thumbnail }`: the key the preset then carries; the settings write that drops the old key deletes its object.
		uploadSourcePresetThumb: builder.mutation({
			query: ({ id, blob }) => {
				const body = new FormData()
				body.append('thumbnail', blob, 'thumb.webp')
				return { url: `user/source-presets/${id}/thumbnail`, method: 'POST', body }
			},
		}),
		// sessions
		getSessions: builder.query({
			query: () => 'sessions',
			transformResponse: res => {
				if (!res) return EMPTY_ARRAY
				return res.sessions
			},
			providesTags: result =>
				result ? [...result.map(({ id }) => ({ type: SESSION, id })), LIST_SESSIONS] : [LIST_SESSIONS],
		}),
		getActiveSession: builder.query({
			query: () => 'sessions?status=launch_requested,launched,pending,migrating,provisioning,started,running',
			keepUnusedDataFor: YEAR,
			transformResponse: res => {
				let { sessions } = res
				if (!sessions) {
					logger.warn('Invalid active session payload', res)
					return EMPTY_SESSION
				}

				if (!sessions.length) {
					return EMPTY_SESSION
				}

				if (sessions.length > 1) {
					logger.warn('getActiveSession', `Multiple active sessions found`, sessions)
					const thirty_min_ago = Date.now() - 30 * 60 * 1000 // 30 minutes in milliseconds

					const fsessions = []
					let s
					for (let i = 0; i < sessions.length; i++) {
						s = sessions[i]
						if (s.status === SESSION_STATUS.RUNNING || s.status === SESSION_STATUS.STARTED) return s

						const createdAt = new Date(s.createdAt).getTime()

						if (createdAt > thirty_min_ago) {
							fsessions.push(s)
						}
					}
					sessions = fsessions
				}

				const session = sessions[0]

				if (session) {
					window.session = session
					window.session_arn = session.extraData.taskArn
					session.statusTimestamp = session.statusChangedAt
				}

				return session
			},
			providesTags: result =>
				result ? [GET_ACTIVE_SESSION, { type: SESSION, id: result.id }] : [GET_ACTIVE_SESSION],
		}),
		// getSession: builder.query({
		// 	query: id => `sessions/${id}`, transformResponse: res => res.session, providesTags: (result, error, id) => [{ type: SESSION, id }], }),
		startSession: builder.mutation({
			query: body => ({
				url: 'sessions',
				method: 'POST',
				body,
			}),
			onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
				dispatch(
					api.util.updateQueryData('getActiveSession', undefined, draft => {
						if (draft) {
							draft.status = SESSION_STATUS.LOADING
						}
					})
				)

				try {
					const { data } = await queryFulfilled
					dispatch(
						api.util.updateQueryData('getActiveSession', undefined, draft => {
							Object.assign(draft, data.session)
							draft.statusTimestamp = data.session.statusChangedAt
						})
					)
					// Notify other clients to refetch active session
					socket.sendToUser(ABLY_MESSAGES.INVALIDATE_API_TAGS, [GET_ACTIVE_SESSION])
				} catch (error) {
					logger.error('startSession', 'Failed to start session', error)
					dispatch(
						api.util.updateQueryData('getActiveSession', undefined, draft => {
							if (draft) {
								draft.status = SESSION_STATUS.IDLE
							}
						})
					)
				}
			},
			transformResponse: res => res,
			invalidatesTags: [LIST_SESSIONS, GET_ACTIVE_SESSION],
		}),
		stopSession: builder.mutation({
			query: id => ({
				url: `sessions/${id}/stop`,
				method: 'POST',
			}),
			onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
				dispatch(
					api.util.updateQueryData('getActiveSession', undefined, draft => {
						if (draft) {
							draft.status = SESSION_STATUS.LOADING
						}
					})
				)
				try {
					const { data } = await queryFulfilled
					dispatch(
						api.util.updateQueryData('getActiveSession', undefined, draft => {
							Object.assign(draft, data)
						})
					)
					// Notify other clients to refetch active session
					socket.sendToUser(ABLY_MESSAGES.INVALIDATE_API_TAGS, [GET_ACTIVE_SESSION])
				} catch (error) {
					logger.error('stopSession', 'Failed to stop session', error)
					dispatch(
						api.util.updateQueryData('getActiveSession', undefined, draft => {
							if (draft) {
								draft.status = SESSION_STATUS.IDLE
							}
						})
					)
				}
			},
			invalidatesTags: (result, error, { id }) => [GET_ACTIVE_SESSION, { type: SESSION, id }],
		}),
		// projects
		getProjects: builder.query({
			query: () => 'projects',
			transformResponse: res => {
				if (!res) return EMPTY_ARRAY
				const p = [...res.projects]
				p.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
				return p
			},
			providesTags: result =>
				result ? [...result.map(({ id }) => ({ type: PROJECT, id })), LIST_PROJECTS] : [LIST_PROJECTS],
		}),
		getProject: builder.query({
			query: id => `projects/${id}`,
			transformResponse: transformProject,
			async onQueryStarted(id, { dispatch, queryFulfilled }) {
				// Update getProjects cache
				logger.info('getProject onQueryStarted', `updating getProjects cache`)
				try {
					const res = await queryFulfilled
					const { project } = res.data
					dispatch(
						api.util.updateQueryData('getProjects', undefined, d => {
							const index = d.findIndex(p => p.id === project.id)
							if (index !== -1) {
								Object.assign(d[index], project)
								d.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
							} else {
								logger.info('getProject', `Project ${project.id} added to getProjects cache`)
								d.push(project)
								d.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
							}
						})
					)
				} catch (error) {
					logger.error('getProject onQueryStarted', `Error fetching project ${id}`, error)
				}
			},
			providesTags: (result, error, id) => [{ type: PROJECT, id }],
		}),
		addProject: builder.mutation({
			query: body => ({
				url: 'projects',
				method: 'POST',
				body,
			}),
			async onQueryStarted(body, { dispatch, queryFulfilled }) {
				const temp_id = `temp_project_${uuid()}`

				const addResult = dispatch(
					api.util.updateQueryData('getProjects', undefined, d => {
						d.unshift({ ...body, id: temp_id })
					})
				)

				try {
					const { data } = await queryFulfilled
					if (!data) {
						throw new Error('No project data received')
					}

					dispatch(
						api.util.updateQueryData('getProjects', undefined, d => {
							const i = d.findIndex(p => p.id === temp_id)
							if (i !== -1) {
								d[i] = data
							}
						})
					)
					logger.info('addProject', `Project created successfully: ${data.id}`)
				} catch (error) {
					logger.error('addProject', 'Failed to create project', error)
					addResult.undo()
				}
			},
			transformResponse: res => res.project,
		}),
		updateProject: builder.mutation({
			query: ({ id, name, presets, settings, pins }) => {
				if (!id) {
					logger.error('updateProject', 'id is required')
					return
				}
				if (!name && !presets && !settings && !pins) return
				const body = {}

				if (name) body.name = name
				if (presets) body.presets = presets.filter(p => !p.includes('temp_'))
				if (settings) body.settings = settings
				if (pins) body.pins = pins

				return {
					url: `projects/${id}`,
					method: 'PATCH',
					body,
				}
			},
			async onQueryStarted({ id, ...patch }, { getState, dispatch, queryFulfilled }) {
				const s = getState()
				const oldCurrentIndex = s.playback.current_preset_index
				const project = selectProject(s)

				const patches = []
				const currentProjectId = selectProjectId(s)
				let currentPresetID
				// is currently open project
				if (currentProjectId === id) {
					// Patch local project state
					patches.push(
						dispatch(
							api.util.updateQueryData('getProject', id, d => {
								if (patch.presets) {
									d.project.presets = patch.presets
								}
								if (patch.name) {
									d.project.name = patch.name
								}
								if (patch.settings) {
									d.project.settings = { ...d.project.settings, ...patch.settings }
								}
								if (patch.pins) d.project.pins = patch.pins
								return d
							})
						)
					)

					if (patch.name) {
						// Update getProjects cache with new name
						dispatch(
							api.util.updateQueryData('getProjects', undefined, d => {
								const i = d.findIndex(p => p.id === id)
								if (i !== -1) d[i].name = patch.name
								return d
							})
						)
					}

					currentPresetID = project.presets[oldCurrentIndex]
					// Update current preset index after shuffle
					if (currentPresetID && 'presets' in patch) {
						const index = patch.presets.findIndex(pid => pid === currentPresetID)
						logger.info('updateProject', `Setting current preset index to ${index}`)
						dispatch(setCurrentPresetIndex(index))
					}
				}

				try {
					const { data } = await queryFulfilled

					// Update getProjects cache with final data
					dispatch(
						api.util.updateQueryData('getProjects', undefined, d => {
							const i = d.findIndex(p => p.id === data.id)
							if (i !== -1) d[i] = { ...data, pins: parsePins(data.pins) }
							return d
						})
					)

					// Update getProject cache with final data Only update fields that were part of this mutation to avoid overwriting concurrent optimistic updates (e.g., from deletePresets)
					dispatch(
						api.util.updateQueryData('getProject', id, d => {
							if (patch.name) d.project.name = data.name
							if (patch.settings) d.project.settings = data.settings
							if (patch.presets) d.project.presets = data.presets
							return d
						})
					)
					logger.info('updateProject', `Project ${id} updated successfully`)
				} catch (error) {
					logger.error('updateProject', `Failed to update project ${id}`, error)
					patches.reverse().forEach(patch => patch.undo())
					if (patch.presets) dispatch(setCurrentPresetIndex(oldCurrentIndex))
				}
			},
			transformResponse: res => res.project,
		}),
		// Set the project thumbnail from one of its presets.
		//
		// The server COPIES the preset's thumbnail object to a project-owned key rather than storing the preset's key. Preset thumbnail keys are versioned ({presetId}~{epochMs}.{ext}) and the old object is deleted on the preset's next save, so a shared key would dangle. Sending { presetId } and letting the server do the copy is what keeps the project thumb stable.
		setProjectThumbnail: builder.mutation({
			query: ({ projectId, presetId }) => ({
				url: `projects/${projectId}/thumbnail`,
				method: 'POST',
				body: { presetId },
			}),
			async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					// Adopt the server's key/URL — the copy lives at a path the client can't predict.
					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							if (!d.project.settings) d.project.settings = {}
							d.project.settings.thumbnail = data.thumbnailKey ?? data.settings?.thumbnail
							d.project.thumbnailKey = data.thumbnailKey ?? null
							d.project.thumbnailUrl = data.thumbnailUrl ?? null
						})
					)
					dispatch(
						api.util.updateQueryData('getProjects', undefined, d => {
							const p = d.find(p => p.id === projectId)
							if (p) {
								if (!p.settings) p.settings = {}
								p.settings.thumbnail = data.thumbnailKey ?? data.settings?.thumbnail
								p.thumbnailKey = data.thumbnailKey ?? null
								p.thumbnailUrl = data.thumbnailUrl ?? null
							}
						})
					)
					logger.info('setProjectThumbnail', `Project ${projectId} thumbnail set`)
				} catch (error) {
					logger.error('setProjectThumbnail', `Failed to set thumbnail for project ${projectId}`, error)
				}
			},
			transformResponse: res => res.project,
		}),
		deleteProject: builder.mutation({
			query: projectId => ({
				url: `projects/${projectId}`,
				method: 'DELETE',
			}),
			async onQueryStarted(id, { dispatch, getState, queryFulfilled }) {
				// Check if deleted project is current project
				const s = getState()
				const currentProjectId = selectProjectId(s)
				const isCurrentProject = currentProjectId === id

				// Get current projects list
				const projects = api.endpoints.getProjects.select()(s).data

				// Don't allow deleting if only one project exists
				if (projects.length <= 1) {
					logger.error('deleteProject', 'Cannot delete last project')
					throw new Error('Cannot delete last project')
				}

				const deleteResult = dispatch(
					api.util.updateQueryData('getProject', id, () => {
						return null
					})
				)

				let remaining

				// Optimistically update projects list and switch project if needed
				const deleteListResult = dispatch(
					api.util.updateQueryData('getProjects', undefined, d => {
						remaining = d.filter(p => p.id !== id)
						return remaining
					})
				)

				if (isCurrentProject) {
					// Switch to the first remaining project using getProject initiate
					logger.info('deleteProject', 'Deleted current project')
					if (remaining.length) {
						logger.info('deleteProject', `Switching to project ${remaining[0].id}`)
						dispatch(setProjectId(remaining[0].id))
					} else {
						logger.info('deleteProject', 'No remaining projects, setting project ID to null')
						dispatch(setProjectId(null))
					}
					// dispatch(api.endpoints.getProj.ect.initiate(remaining[0].id))
				}

				try {
					await queryFulfilled
					logger.info('deleteProject', `Project ${id} deleted successfully`)
				} catch (err) {
					logger.error('deleteProject', `Failed to delete project ${id}`, err)
					deleteListResult.undo()
					deleteResult.undo()
					if (isCurrentProject) {
						// If undoing, switch back to the original project dispatch(api.endpoints.getProj.ect.initiate(id))
						dispatch(setProjectId(id))
					}
				}
			},
		}),
		deleteProjects: builder.mutation({
			query: ids => ({
				url: `projects`,
				method: 'DELETE',
				body: { ids },
			}),
			async onQueryStarted(ids, { dispatch, getState, queryFulfilled }) {
				const s = getState()
				const currentProjectId = selectProjectId(s)
				const projects = api.endpoints.getProjects.select()(s).data

				if (!projects?.length) {
					logger.error('deleteProjects', 'No projects data available')
					throw new Error('Cannot delete projects: data not loaded')
				}

				const idsSet = new Set(ids)

				// Prevent deletion if it would remove all projects
				const remaining = projects.filter(p => !idsSet.has(p.id))
				if (remaining.length === 0) {
					logger.error('deleteProjects', 'Cannot delete all projects')
					throw new Error('Cannot delete all projects')
				}

				// Optimistically update getProject cache for each deleted ID
				const deleteResults = ids.map(id => dispatch(api.util.updateQueryData('getProject', id, () => null)))

				// Optimistically update projects list
				const deleteListResult = dispatch(
					api.util.updateQueryData('getProjects', undefined, d => d.filter(p => !idsSet.has(p.id)))
				)

				// Switch project if current is being deleted
				const isCurrentDeleted = idsSet.has(currentProjectId)
				if (isCurrentDeleted && remaining.length) {
					dispatch(setProjectId(remaining[0].id))
				}

				try {
					await queryFulfilled
					logger.info('deleteProjects', `Deleted ${ids.length} projects`)
				} catch (err) {
					logger.error('deleteProjects', 'Failed to delete projects', err)
					deleteListResult.undo()
					deleteResults.forEach(r => r.undo())
					if (isCurrentDeleted) {
						dispatch(setProjectId(currentProjectId))
					}
				}
			},
		}),
		cloneProject: builder.mutation({
			query: ({ id, name }) => ({
				url: `projects/${id}/clone`,
				method: 'POST',
				body: { name },
			}),
			async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					dispatch(
						api.util.updateQueryData('getProjects', undefined, d => {
							d.unshift(data.project)
						})
					)
				} catch (err) {
					logger.error('cloneProject', `Failed to clone project ${id}`, err)
				}
			},
			transformResponse: res => res,
		}),
		// presets getPresets: builder.query({
		// ^ Obsolete query: projectId => `projects/${projectId}/presets`, transformResponse: res => {
		// 		if (!res || !res.presets) return EMPTY_ARRAY return res.presets.map(conformPresetSettings) }, providesTags: (res, err, { projectId }) => [ ...(res ? res.map(({ id }) => ({ type: PRESET, id })) : []), { type: PRESET, id: projectId }, ], }),
		getPreset: builder.query({
			query: ({ projectId, id }) => `projects/${projectId}/presets/${id}`,
			transformResponse: res => {
				if (!res?.preset) return null
				return conformPresetSettings(res.preset)
			},
			providesTags: (result, error, { id }) => [{ type: PRESET, id }],
		}),
		addPreset: builder.mutation({
			query: ({ projectId, thumbnail, ...body }) => {
				// If thumbnail blob provided, use multipart form data
				if (thumbnail) {
					const formData = new FormData()
					if (body.name) formData.append('name', body.name)
					if (body.settings) formData.append('settings', JSON.stringify(body.settings))
					if (body.orderIndex !== undefined) formData.append('orderIndex', String(body.orderIndex))
					formData.append('thumbnail', thumbnail, 'thumb.jpg')
					return {
						url: `projects/${projectId}/presets`,
						method: 'POST',
						body: formData,
					}
				}
				// Otherwise use JSON
				return {
					url: `projects/${projectId}/presets`,
					method: 'POST',
					body,
				}
			},
			async onQueryStarted(
				{ projectId, orderIndex, thumbnail, thumbnailUrl: existingThumbnailUrl, ...body },
				{ getState, dispatch, queryFulfilled }
			) {
				// Get original state BEFORE optimistic updates
				const s = getState()
				const old_index = s.playback.current_preset_index
				const originalSelection = s.selection.ids
				// const was_current = orderIndex - 1 === old_index

				logger.info('addPreset', `Adding preset to project ${projectId}`)

				// Create temporary new preset
				const temp_preset_id = `temp_preset_${uuid()}`
				// Reuse existing blob URL if passed (from add cell), otherwise create new one
				const tempThumbnailUrl = existingThumbnailUrl || (thumbnail ? URL.createObjectURL(thumbnail) : null)
				// ⚠️ The conform lives INSIDE the writer, not at its call sites: this bypasses `transformResponse`, so it is a cache door in its own right, and putting the call here makes a caller that forgets it structurally impossible. `pasteAction` relies on exactly this — it passes untrusted clipboard data straight through as an ordinary caller.
				const optimisticPreset = {
					...body,
					settings: conform(body.settings),
					id: temp_preset_id,
					_tempId: temp_preset_id,
					thumbnailUrl: tempThumbnailUrl,
				}
				const optimisticPatches = [] // Store optimistic patch results for undo

				// 1. Optimistic: Update getProject (add temp ID to project.presets list, and push temp preset to getProject)
				optimisticPatches.push(
					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							const insertIndex =
								orderIndex !== undefined && orderIndex !== null ? orderIndex : d.presets.length

							// Add temp ID to project.presets list
							d.project.presets.splice(insertIndex, 0, temp_preset_id)

							// Push temp preset to getProject
							d.presets.push(optimisticPreset)
						})
					)
				)

				// 2. Optimistic: Set the current preset index to the new temp preset index
				const new_index = orderIndex ?? old_index + 1
				// 3. Optimistic: Select the new temp preset
				dispatch(setSelection({ focus: 'preset', ids: [temp_preset_id] }))
				dispatch(setCurrentPresetIndex(new_index))

				// Note: We don't save thumbnail to cache with temp_id because:
				// 1. RTK cache already has the blob URL (tempThumbnailUrl) for immediate display
				// 2. setThumbnail creates a new blob URL, causing flicker when it notifies PresetStrip The thumbnail will be saved to IndexedDB with the real_id after server confirms

				// 4. QueryFulfilled - Get the real preset from the backend
				try {
					const { data } = await queryFulfilled
					logger.info('addPreset', `Preset added successfully`)

					const { project, preset: rawPreset } = data

					if (!rawPreset?.id) throw new Error('No valid preset returned from backend')

					// `conform` owns the string-parse (settings arrive as a string when sent via FormData with a thumbnail). Also transform thumbnail -> thumbnailUrl for cache consistency.
					const preset = {
						...rawPreset,
						_tempId: temp_preset_id, // Preserve for stable React key across temp->real transition
						settings: conform(rawPreset.settings),
						// Keep using local blob URL for immediate display (avoids server fetch flicker) Fall back to server URL if no local URL exists
						thumbnailUrl: tempThumbnailUrl || rawPreset.thumbnailUrl || rawPreset.thumbnail || null,
						thumbnailKey: rawPreset.thumbnailKey || null,
					}
					delete preset.thumbnail

					// Update getProject cache
					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							const index = d.presets.findIndex(p => p.id === temp_preset_id)
							if (index !== -1) {
								d.presets[index] = preset
							} else {
								logger.warn('addPreset', `Temp ID ${temp_preset_id} not found in cache`)
								return
							}

							d.project = project
						})
					)

					// Set project thumbnail from preset only if project has none.
					//
					// Goes through setProjectThumbnail (server copies to a project-owned `project-thumb~{ts}` key) — NOT updateProject({ settings: { thumbnail: key } }). Preset keys are versioned and the old object is deleted on the preset's next save, so storing a preset's key here would dangle and the project thumb would 404. The optimistic patch below shows the preset's image immediately; the mutation's onQueryStarted swaps in the real project-owned key/URL when the copy lands.
					const serverThumbKey = rawPreset.thumbnailKey || null
					const currentProjectThumb =
						getState().api.queries[`getProject("${projectId}")`]?.data?.project?.thumbnailKey
					if (serverThumbKey && !currentProjectThumb) {
						dispatch(
							api.util.updateQueryData('getProject', projectId, d => {
								if (!d.project.settings) d.project.settings = {}
								d.project.settings.thumbnail = serverThumbKey
								d.project.thumbnailKey = serverThumbKey
								d.project.thumbnailUrl = rawPreset.thumbnailUrl || null
							})
						)
						dispatch(api.endpoints.setProjectThumbnail.initiate({ projectId, presetId: rawPreset.id }))
					}

					dispatch(setSelection({ focus: 'preset', ids: [preset.id] }))

					// Persist thumbnail to IndexedDB with REAL ID for offline/refresh support Pass existing blob URL to reuse (prevents flicker from URL change)
					if (thumbnail) {
						setThumbnail(preset.id, projectId, thumbnail, tempThumbnailUrl)
						// Note: tempThumbnailUrl is now owned by urlCache[preset.id], don't revoke Clean up temp entry (no-op since we don't save temp_id to cache anymore)
						removePresetThumbnail(temp_preset_id)
					}
				} catch (error) {
					logger.error('addPreset', 'Failed to add preset', error)
					// Clean up temp blob URL on error - only if we created it (not reused)
					if (tempThumbnailUrl && !existingThumbnailUrl) {
						URL.revokeObjectURL(tempThumbnailUrl)
					}
					optimisticPatches.reverse().forEach(patch => patch.undo())
					const currentState = getState()
					if (
						currentState.playback.current_preset_index !== old_index ||
						JSON.stringify(currentState.selection.ids) !== JSON.stringify(originalSelection)
					) {
						logger.info('addPreset', 'Reverting playback state due to error')
						dispatch(setCurrentPresetIndex(old_index))
						dispatch(setSelection({ focus: 'preset', ids: originalSelection }))
					}
				}
			},
		}),
		addPresets: builder.mutation({
			query: ({ projectId, presets }) => ({
				url: `projects/${projectId}/presets`,
				method: 'POST',
				body: presets,
			}),

			async onQueryStarted({ projectId, presets }, { dispatch, getState, queryFulfilled }) {
				const s = getState()
				const originalSelection = s.selection.ids
				const old_index = s.playback.current_preset_index

				logger.info('addPresets', `Adding ${presets.length} presets to project ${projectId}`)

				const temp_ids = presets.map(() => `temp_preset_${uuid()}`)
				// ⚠️ Conformed INSIDE the writer, like `addPreset`/`updatePreset`. This is the endpoint `pasteAction` uses, so it is the door untrusted clipboard data actually comes through.
				const optimisticPresets = presets.map((p, i) => ({
					...p,
					settings: conform(p.settings),
					id: temp_ids[i],
				}))
				const updates = []

				// Optimistic: Update getProject cache
				updates.push(
					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							const insertIndex =
								presets[0].orderIndex !== undefined ? presets[0].orderIndex : d.presets.length

							// Add temp IDs to project.presets list
							d.project.presets.splice(insertIndex, 0, ...temp_ids)

							// Push temp presets to presets array
							d.presets.push(...optimisticPresets)
						})
					)
				)

				dispatch(setSelection({ focus: 'preset', ids: temp_ids }))

				const insertIndex = presets[0].orderIndex
				if (insertIndex <= old_index) {
					dispatch(setCurrentPresetIndex(old_index + presets.length))
				}

				try {
					const { data } = await queryFulfilled
					if (!data?.presets?.length) {
						throw new Error('No presets data received')
					}

					// Update getProject cache with real data
					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							data.presets.forEach((preset, i) => {
								// Replace temp ID in project.presets
								const projectIndex = d.project.presets.findIndex(p => p === temp_ids[i])
								if (projectIndex !== -1) {
									d.project.presets[projectIndex] = preset.id
								}

								// Replace temp preset in presets array
								const presetIndex = d.presets.findIndex(p => p.id === temp_ids[i])
								if (presetIndex !== -1) {
									const oldThumb = d.presets[presetIndex].thumbnailUrl
									d.presets[presetIndex] = { ...preset, settings: conform(preset.settings) }
									if (oldThumb && preset.thumbnailUrl) {
										d.presets[presetIndex].thumbnailUrl = oldThumb
									}
								}
							})
						})
					)

					dispatch(setSelection({ focus: 'preset', ids: data.presets.map(p => p.id) }))

					logger.info('addPresets', `${data.presets.length} presets added successfully`)
				} catch (error) {
					logger.error('addPresets', 'Failed to add presets', error)
					dispatch(setSelection({ focus: 'preset', ids: originalSelection }))

					updates.reverse().forEach(update => update.undo())

					dispatch(setCurrentPresetIndex(old_index))

					throw error
				}
			},
		}),
		updatePreset: builder.mutation({
			query: ({ projectId, thumbnail, ...body }) => {
				// If thumbnail blob provided, use multipart form data
				if (thumbnail) {
					const formData = new FormData()
					if (body.name) formData.append('name', body.name)
					if (body.settings) formData.append('settings', JSON.stringify(body.settings))
					formData.append('thumbnail', thumbnail, 'thumb.jpg')
					return {
						url: `projects/${projectId}/presets/${body.id}`,
						method: 'PATCH',
						body: formData,
					}
				}
				// Otherwise use JSON
				return {
					url: `projects/${projectId}/presets/${body.id}`,
					method: 'PATCH',
					body,
				}
			},
			async onQueryStarted({ projectId, id, ...body }, { getState, dispatch, queryFulfilled }) {
				logger.info('updatePreset', `Updating preset ${id} in project ${projectId}`)

				// Optimistic update the getProject cache
				const patchProjectResult = dispatch(
					api.util.updateQueryData('getProject', projectId, d => {
						const preset = d.presets.find(p => p.id === id)
						if (!preset) {
							logger.warn('updatePreset', `Preset ${id} not found in cache`)
							return
						}
						if (body.name) preset.name = body.name
						if (body.settings) {
							const lockedObjs = Object.entries(body.settings.objects || {}).filter(([, o]) => o?.locked)
							logger.info(
								'[updatePreset cache] locked objects:',
								lockedObjs.map(([id]) => id)
							)
							// ⚠️ Inside the writer, for the same reason as `addPreset`: this bypasses `transformResponse`. Redundant-looking on a save — `live` is already well-formed — but that is exactly its value here, as the WRITE-side validator: a morph envelope at rest, an un-pruned key or a legacy null is caught BEFORE it reaches the DB rather than after.
							preset.settings = conform(body.settings)
						}
					})
				)

				try {
					const { data } = await queryFulfilled
					logger.info('updatePreset', `Preset ${id} updated successfully`)

					// Update cache with response data (including new thumbnailUrl)
					if (data?.preset) {
						dispatch(
							api.util.updateQueryData('getProject', projectId, d => {
								const preset = d.presets.find(p => p.id === id)
								if (preset) {
									preset.thumbnailUrl = data.preset.thumbnailUrl || data.preset.thumbnail || null
									preset.thumbnailKey = data.preset.thumbnailKey || null
								}
							})
						)

						// Set project thumbnail from preset only if project has none. See the matching block in addPreset: must go through setProjectThumbnail so the project gets its OWN object. Storing this preset's key would dangle on the preset's next save (versioned keys, old object deleted).
						const currentThumb =
							getState().api.queries[`getProject("${projectId}")`]?.data?.project?.thumbnailKey
						if (data.preset.thumbnailKey && !currentThumb) {
							dispatch(
								api.util.updateQueryData('getProject', projectId, d => {
									if (!d.project.settings) d.project.settings = {}
									d.project.settings.thumbnail = data.preset.thumbnailKey
									d.project.thumbnailKey = data.preset.thumbnailKey
									d.project.thumbnailUrl = data.preset.thumbnailUrl || null
								})
							)
							dispatch(api.endpoints.setProjectThumbnail.initiate({ projectId, presetId: id }))
						}
					}
				} catch (error) {
					logger.error('updatePreset', `Failed to update preset ${id}`, error)
					patchProjectResult.undo()
				}
			},
		}),
		/* deletePreset: builder.mutation({
			query: ({ projectId, id }) => ({
				url: `projects/${projectId}/presets/${id}`,
				method: 'DELETE',
			}),
			async onQueryStarted({ projectId, id }, { getState, dispatch, queryFulfilled }) {
				const s = getState()
				const { current_preset_index } = s.playback
				const presets = selectPresets(s)
				const deleted_index = presets.findIndex(p => p.id === id)

				logger.info('deletePreset', `Deleting preset ${id} from project ${projectId}`)

				const patches = []

				patches.push(
					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							const { project, presets } = d
							if (!project) {
								logger.warn('deletePreset', 'Project not found in getProject cache')
								return
							}
							// Remove from order array (check index to avoid splice(-1) bug)
							const orderIndex = project.presets.findIndex(p => p === id)
							if (orderIndex !== -1) project.presets.splice(orderIndex, 1)
							// Remove from data array
							const dataIndex = presets.findIndex(p => p.id === id)
							if (dataIndex !== -1) presets.splice(dataIndex, 1)
						})
					)
				)

				if (deleted_index <= current_preset_index) {
					logger.info('deletePreset', `Setting index to ${Math.max(0, current_preset_index - 1)}`)
					dispatch(loadPreset({ index: Math.max(0, current_preset_index - 1), advance: true }))
				}

				try {
					await queryFulfilled
					logger.info('deletePreset', `Preset ${id} deleted successfully`)

					// Clear thumbnail from local cache
					removePresetThumbnail(id)

					// Update project thumbnail to first remaining preset's thumbnail
					const updatedState = getState()
					const projectData = updatedState.api.queries[`getProject("${projectId}")`]?.data
					const remainingPresets = projectData?.presets || []
					const firstPresetWithThumb = remainingPresets.find(p => p.thumbnailKey)
					const newThumb = firstPresetWithThumb?.thumbnailKey || null
					const newThumbUrl = firstPresetWithThumb?.thumbnailUrl || null

					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							if (!d.project.settings) d.project.settings = {}
							d.project.settings.thumbnail = newThumb
							d.project.thumbnailKey = newThumb
							d.project.thumbnailUrl = newThumbUrl
						})
					)
					// Re-point at a surviving preset via setProjectThumbnail so the project gets its own copy — storing the preset's (versioned) key would dangle on its next save. When no preset has a thumbnail left there is nothing to copy, so clear the field directly;
					// setProjectThumbnail requires a presetId and cannot express "none".
					if (firstPresetWithThumb) {
						dispatch(
							api.endpoints.setProjectThumbnail.initiate({
								projectId,
								presetId: firstPresetWithThumb.id,
							})
						)
					} else {
						dispatch(
							api.endpoints.updateProject.initiate({
								id: projectId,
								settings: { thumbnail: null },
							})
						)
					}
				} catch (error) {
					logger.error('deletePreset', `Failed to delete preset ${id}`, error)
					patches.reverse().forEach(patch => patch.undo())
					dispatch(setCurrentPresetIndex(current_preset_index))
				}
			},
		}), */
		deletePresets: builder.mutation({
			query: ({ projectId, ids }) => ({
				url: `projects/${projectId}/presets`,
				method: 'DELETE',
				body: { ids },
			}),
			async onQueryStarted({ projectId, ids }, { getState, dispatch, queryFulfilled }) {
				logger.info('deletePresets', `Deleting ${ids.length} presets from project ${projectId}`)
				const idsSet = new Set(ids)

				const state = getState()
				const { current_preset_index } = state.playback
				const presets = selectPresets(state)

				const deletedIndices = presets.reduce((acc, p, i) => {
					if (idsSet.has(p.id)) acc.push(i)
					return acc
				}, [])

				const minDeletedIndex = Math.min(...deletedIndices)
				const maxDeletedIndex = minDeletedIndex + deletedIndices.length - 1

				const updateProjectResult = dispatch(
					api.util.updateQueryData('getProject', projectId, d => {
						d.project.presets = d.project.presets.filter(p => !idsSet.has(p))
						d.presets = d.presets.filter(p => !idsSet.has(p.id))
					})
				)

				// Handle current preset index and selection after deletion
				const remainingCount = presets.length - deletedIndices.length
				const remainingPresets = presets.filter(p => !idsSet.has(p.id))

				if (remainingCount === 0) {
					// All user presets deleted - fall back to first template preset
					dispatch(setSelection({ focus: 'preset', ids: [] }))
					dispatch(loadFirstTemplatePreset())
				} else if (current_preset_index > maxDeletedIndex) {
					// Current preset is AFTER deleted range - just shift index (same preset, no reload needed)
					const newIndex = current_preset_index - deletedIndices.length
					dispatch(setCurrentPresetIndex(newIndex))
					dispatch(setSelection({ focus: 'preset', ids: [remainingPresets[newIndex]?.id].filter(Boolean) }))
				} else if (current_preset_index >= minDeletedIndex) {
					// Current preset WAS DELETED - need to load the preset that slid into this position Use force: true because index may be same but preset at that index changed
					let newIndex
					if (maxDeletedIndex < presets.length - 1) {
						// Presets exist after deleted range - load first one after
						newIndex = minDeletedIndex
					} else {
						// Deleting at end - load previous preset
						newIndex = Math.max(0, minDeletedIndex - 1)
					}
					dispatch(setSelection({ focus: 'preset', ids: [remainingPresets[newIndex]?.id].filter(Boolean) }))
					dispatch(loadPreset({ index: newIndex, confirmed: true, force: true, advance: true }))
				} else {
					// Current was before deleted range - no index change needed, just update selection
					dispatch(
						setSelection({ focus: 'preset', ids: [presets[current_preset_index]?.id].filter(Boolean) })
					)
				}

				try {
					await queryFulfilled
					logger.info('deletePresets', `${ids.length} presets deleted successfully`)

					// Clear thumbnails from local cache
					ids.forEach(id => removePresetThumbnail(id))

					// Update project thumbnail to first remaining preset's thumbnail
					const updatedState = getState()
					const projectData = updatedState.api.queries[`getProject("${projectId}")`]?.data
					const remainingPresets = projectData?.presets || []
					const firstPresetWithThumb = remainingPresets.find(p => p.thumbnailKey)
					const newThumb = firstPresetWithThumb?.thumbnailKey || null
					const newThumbUrl = firstPresetWithThumb?.thumbnailUrl || null

					dispatch(
						api.util.updateQueryData('getProject', projectId, d => {
							if (!d.project.settings) d.project.settings = {}
							d.project.settings.thumbnail = newThumb
							d.project.thumbnailKey = newThumb
							d.project.thumbnailUrl = newThumbUrl
						})
					)
					// Re-point at a surviving preset via setProjectThumbnail so the project gets its own copy — storing the preset's (versioned) key would dangle on its next save. When no preset has a thumbnail left there is nothing to copy, so clear the field directly;
					// setProjectThumbnail requires a presetId and cannot express "none".
					if (firstPresetWithThumb) {
						dispatch(
							api.endpoints.setProjectThumbnail.initiate({
								projectId,
								presetId: firstPresetWithThumb.id,
							})
						)
					} else {
						dispatch(
							api.endpoints.updateProject.initiate({
								id: projectId,
								settings: { thumbnail: null },
							})
						)
					}
				} catch (error) {
					logger.error('deletePresets', `Failed to delete presets`, error)
					updateProjectResult.undo()
					dispatch(setCurrentPresetIndex(current_preset_index))
				}
			},
		}),
		// templates
		getTemplates: builder.query({
			query: () => templateEndpointName,
			transformResponse: res => {
				if (!res) return EMPTY_ARRAY
				const temps = res.projectTemplates.map(({ id, name, description, thumbnail, thumbnailUrl, tags }) => ({
					id,
					name,
					description,
					thumbnail,
					thumbnailUrl,
					tags,
				}))
				temps.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
				return temps
			},
			providesTags: result =>
				result ? [...result.map(({ id }) => ({ type: TEMPLATE, id })), LIST_TEMPLATES] : [LIST_TEMPLATES],
		}),
		getTemplate: builder.query({
			query: id => `${templateEndpointName}/${id}`,
			transformResponse: res => {
				if (!res) return EMPTY_ARRAY
				return res.projectTemplate
			},
			providesTags: (result, error, id) => [{ type: TEMPLATE, id }],
		}),
		addTemplate: builder.mutation({
			query: ({ thumbnail, ...body }) => {
				// If thumbnail blob provided, use multipart form data
				if (thumbnail instanceof Blob) {
					const formData = new FormData()
					if (body.name) formData.append('name', body.name)
					if (body.description) formData.append('description', body.description)
					if (body.projectId) formData.append('projectId', body.projectId)
					if (body.tags) formData.append('tags', JSON.stringify(body.tags))
					if (body.manifest) formData.append('manifest', JSON.stringify(body.manifest))
					formData.append('thumbnail', thumbnail, 'thumb.jpg')
					return {
						url: templateEndpointName,
						method: 'POST',
						body: formData,
					}
				}
				// Otherwise use JSON (thumbnail is URL string or empty)
				return {
					url: templateEndpointName,
					method: 'POST',
					body: { thumbnail, ...body },
				}
			},
			transformResponse: res => res.projectTemplate,
			invalidatesTags: [LIST_TEMPLATES],
		}),
		updateTemplate: builder.mutation({
			query: ({ id, thumbnail, ...body }) => {
				// If thumbnail blob provided, use multipart form data
				if (thumbnail instanceof Blob) {
					const formData = new FormData()
					if (body.name) formData.append('name', body.name)
					if (body.description) formData.append('description', body.description)
					if (body.tags) formData.append('tags', JSON.stringify(body.tags))
					formData.append('thumbnail', thumbnail, 'thumb.jpg')
					return {
						url: `${templateEndpointName}/${id}`,
						method: 'PATCH',
						body: formData,
					}
				}
				// Otherwise use JSON (thumbnail is URL string or empty)
				return {
					url: `${templateEndpointName}/${id}`,
					method: 'PATCH',
					body: { thumbnail, ...body },
				}
			},
			// TODO: figure out why we're excluding thumbnail here
			async onQueryStarted({ id, thumbnail: _thumbnail, ...body }, { dispatch, queryFulfilled }) {
				// Optimistically update the getTemplates cache
				const patchResult = dispatch(
					api.util.updateQueryData('getTemplates', undefined, d => {
						const template = d.find(t => t.id === id)
						if (template) {
							Object.assign(template, body)
						}
					})
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
			transformResponse: res => res.projectTemplate,
			invalidatesTags: (result, error, { id }) => [{ type: TEMPLATE, id }],
		}),
		deleteTemplate: builder.mutation({
			query: id => ({
				url: `${templateEndpointName}/${id}`,
				method: 'DELETE',
			}),
			onQueryStarted(id, { dispatch, queryFulfilled }) {
				logger.info('deleteTemplate', `Deleting template ${id}`)
				const deleteResult = dispatch(
					api.util.updateQueryData('getTemplates', undefined, d => d.filter(t => t.id !== id))
				)
				queryFulfilled
					.then(() => {
						logger.info('deleteTemplate', `Template ${id} deleted successfully`)
					})
					.catch(error => {
						logger.error('deleteTemplate', `Failed to delete template ${id}`, error)
						deleteResult.undo()
					})
			},
		}),
		// Sync a template's presets from a source project (admin only)
		syncTemplateFromProject: builder.mutation({
			query: ({ templateId, projectId, name, description, tags, manifest }) => ({
				url: `${templateEndpointName}/${templateId}`,
				method: 'PUT',
				body: { projectId, name, description, tags, manifest },
			}),
			transformResponse: res => res.projectTemplate,
			async onQueryStarted({ templateId }, { queryFulfilled }) {
				logger.info('syncTemplateFromProject', `Syncing template ${templateId}`)
				try {
					await queryFulfilled
					logger.info('syncTemplateFromProject', `Template ${templateId} synced successfully`)
				} catch (error) {
					logger.error('syncTemplateFromProject', `Failed to sync template ${templateId}`, error)
				}
			},
			invalidatesTags: (result, error, { templateId }) => [
				{ type: TEMPLATE, id: templateId },
				{ type: TEMPLATE, id: 'mobile_presets' },
				{ type: TEMPLATE, id: 'desktop_presets' },
				LIST_TEMPLATES,
			],
		}),
		saveTemplateAs: builder.mutation({
			query: ({ id, name }) => ({
				url: `${templateEndpointName}/${id}/clone`,
				method: 'POST',
				body: {
					name,
				},
			}),
			transformResponse: res => {
				return res.project
			},
			async onQueryStarted(id, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					if (data?.id) {
						dispatch(setProjectId(data.id))
					} else {
						logger.error('saveTemplateAs: No project ID returned', data)
					}
				} catch (error) {
					logger.error('saveTemplateAs: Failed to load new project', error)
				}
			},
			invalidatesTags: [LIST_PROJECTS],
		}),
		// Clone a template into a new project without any navigation side effects. The caller is responsible for dispatching setProjectId with the result.
		cloneTemplate: builder.mutation({
			query: ({ id, name }) => ({
				url: `${templateEndpointName}/${id}/clone`,
				method: 'POST',
				body: { name },
			}),
			transformResponse: res => res.project,
			invalidatesTags: [LIST_PROJECTS],
		}),
		openExampleTemplate: builder.mutation({
			query: ({ variant }) => ({
				url: `${templateEndpointName}/open-example/${variant}`,
				method: 'POST',
			}),
			transformResponse: res => res.project,
		}),
		saveExampleTemplate: builder.mutation({
			query: ({ variant, name, manifest }) => ({
				url: `${templateEndpointName}/save-example/${variant}`,
				method: 'PUT',
				body: { name, manifest },
			}),
			transformResponse: res => res.projectTemplate,
			invalidatesTags: result => [
				...(result?.id ? [{ type: TEMPLATE, id: result.id }] : []),
				{ type: TEMPLATE, id: 'mobile_presets' },
				{ type: TEMPLATE, id: 'desktop_presets' },
				LIST_TEMPLATES,
			],
		}),
		// Get presets from the named mobile template (for display alongside user presets) TODO: stop using 'named' system on backend, it's neither this nor that.
		getExamplePresets: builder.query({
			query: () => `${templateEndpointName}/mobile?named=true`,
			transformResponse: res => {
				const { project: projectData, presets } = res?.projectTemplate?.project || {}
				if (!presets?.length) return EMPTY_ARRAY

				// Build preset map for lookup
				const presetMap = {}
				for (const p of presets) {
					if (p.presetId) presetMap[p.presetId] = p
				}

				// Use project.presets order array if available, otherwise use presets as-is
				const orderArray = projectData?.presets
				let orderedPresets = presets
				if (orderArray?.length) {
					const fromOrder = orderArray.map(id => presetMap[id]).filter(Boolean)
					if (fromOrder.length) {
						orderedPresets = fromOrder
					} else {
						logger.error(
							'getExamplePresets',
							'Order array IDs do not match preset IDs — using unordered fallback'
						)
					}
				}

				return orderedPresets.map(({ presetId: id, projectId, name, settings, thumbnailUrl }) =>
					conformPresetSettings({ id, projectId, name, settings, thumbnailUrl: thumbnailUrl || null })
				)
			},
			providesTags: [{ type: TEMPLATE, id: 'mobile_presets' }],
		}),
		// Get presets from the named desktop template (for display in the Example Presets panel)
		getDesktopExamplePresets: builder.query({
			query: () => `${templateEndpointName}/desktop?named=true`,
			transformResponse: res => {
				const { project: projectData, presets } = res?.projectTemplate?.project || {}
				if (!presets?.length) return EMPTY_ARRAY

				// Build preset map for lookup
				const presetMap = {}
				for (const p of presets) {
					if (p.presetId) presetMap[p.presetId] = p
				}

				// Use project.presets order array if available, otherwise use presets as-is
				const orderArray = projectData?.presets
				let orderedPresets = presets
				if (orderArray?.length) {
					const fromOrder = orderArray.map(id => presetMap[id]).filter(Boolean)
					if (fromOrder.length) {
						orderedPresets = fromOrder
					} else {
						logger.error(
							'getDesktopExamplePresets',
							'Order array IDs do not match preset IDs — using unordered fallback'
						)
					}
				}

				return orderedPresets.map(({ presetId: id, projectId, name, settings, thumbnailUrl }) =>
					conformPresetSettings({ id, projectId, name, settings, thumbnailUrl: thumbnailUrl || null })
				)
			},
			providesTags: [{ type: TEMPLATE, id: 'desktop_presets' }],
		}),
		// credits
		getCredits: builder.query({
			query: () => 'user/credit',
			transformResponse: res => parseInt(res.amount) || 0,
			providesTags: [CREDITS],
		}),
		createPaymentIntent: builder.mutation({
			query: packageId => ({
				url: 'billing/createPaymentIntent',
				method: 'POST',
				body: { packageId },
			}),
			transformResponse: res => res.clientSecret,
		}),
		getCreditPackages: builder.query({
			query: () => ({
				url: 'billing/packages',
				method: 'GET',
			}),
			transformResponse: res => {
				res.sort((a, b) => a.price - b.price)
				return res
			},
		}),
		// recordings
		getRecordings: builder.query({
			query: () => 'recordings',
			transformResponse: res => {
				const ONE_HOUR = 60 * 60 * 1000
				// serverNow(): compared against server-issued `end` timestamps (stale-merge filter).
				const now = serverNow()

				const recordings = res.recordings
					.map(
						({
							id,
							sessionId,
							name,
							startedAt,
							endedAt,
							thumbnailUrl,
							duration,
							size,
							status,
							sourceKey,
							downloadUrl,
							sourceDownloadUrl,
							isPublic,
							publishedAt,
							publicUrl,
							mergeProgress,
						}) => {
							const start = new Date(startedAt).getTime()
							const end = endedAt ? new Date(endedAt).getTime() : null

							// Filter out failed recordings
							if (status === RECORDING_STATUS.FAILED) {
								logger.warn('getRecordings', `Recording ${id} failed, filtering out`)
								return false
							}

							// Filter out recordings stuck in the merge pipeline (ENDED/MERGING/STOP_REQUESTED older than 1h). A merge that died never reaches MERGED, so without this it would pin RecordingsPanel's poll gate forever. STARTED recordings can last hours, so never filter them by age.
							const STALE_PENDING = [
								RECORDING_STATUS.ENDED,
								RECORDING_STATUS.MERGING,
								RECORDING_STATUS.STOP_REQUESTED,
							]
							if (STALE_PENDING.includes(status) && end && now - end > ONE_HOUR) {
								logger.warn(
									'getRecordings',
									`Recording ${id} is stale (${status}, ended ${Math.round((now - end) / 60000)} min ago but not merged), filtering out`
								)
								return false
							}

							return {
								id,
								sessionId,
								name: name.replace(/:/g, '_'),
								start,
								end,
								duration,
								thumbnailUrl,
								size,
								status,
								sourceKey,
								downloadUrl,
								sourceDownloadUrl,
								isPublic: !!isPublic,
								publishedAt,
								publicUrl,
								mergeProgress,
							}
						}
					)
					.filter(Boolean)

				// Sort by newest first
				recordings.sort((a, b) => (a.start < b.start ? 1 : -1))

				// Enforce constraint: only one STARTED recording should exist
				const startedRecordings = recordings.filter(r => r.status === RECORDING_STATUS.STARTED)
				if (startedRecordings.length > 1) {
					const msg = `Multiple STARTED recordings found (${startedRecordings.length}): ${startedRecordings.map(r => r.id).join(', ')}`
					logger.error('getRecordings', msg)
					logger.log('warn', msg, { recordingIds: startedRecordings.map(r => r.id) })
					// Keep only the most recent (first after sort)
					const keepId = startedRecordings[0].id
					recordings.forEach((r, i) => {
						if (r.status === RECORDING_STATUS.STARTED && r.id !== keepId) {
							recordings[i] = null // Mark for removal
						}
					})
				}

				// Enforce constraint: only one ENDED recording should exist
				const endedRecordings = recordings.filter(r => r && r.status === RECORDING_STATUS.ENDED)
				if (endedRecordings.length > 1) {
					const msg = `Multiple ENDED recordings found (${endedRecordings.length}): ${endedRecordings.map(r => r.id).join(', ')}`
					logger.error('getRecordings', msg)
					logger.log('warn', msg, { recordingIds: endedRecordings.map(r => r.id) })
					// Keep only the most recent (first after sort)
					const keepId = endedRecordings[0].id
					recordings.forEach((r, i) => {
						if (r && r.status === RECORDING_STATUS.ENDED && r.id !== keepId) {
							recordings[i] = null // Mark for removal
						}
					})
				}

				// Remove nulls from duplicate filtering
				return recordings.filter(Boolean)
			},
			providesTags: result =>
				result ? [...result.map(({ id }) => ({ type: RECORDING, id })), LIST_RECORDINGS] : [LIST_RECORDINGS],
		}),
		getRecording: builder.query({
			query: ({ sessionId, recordingId }) => `sessions/${sessionId}/recordings/${recordingId}`,
			transformResponse: res => {
				const r = res.recording
				return {
					id: r.id,
					sessionId: r.sessionId,
					name: r.name?.replace(/:/g, '_'),
					start: new Date(r.startedAt).getTime(),
					end: r.endedAt ? new Date(r.endedAt).getTime() : null,
					duration: r.duration,
					thumbnailUrl: r.thumbnailUrl,
					size: r.size,
					status: r.status,
					sourceKey: r.sourceKey,
					downloadUrl: r.downloadUrl,
					sourceDownloadUrl: r.sourceDownloadUrl,
					isPublic: !!r.isPublic,
					publishedAt: r.publishedAt,
					publicUrl: r.publicUrl,
				}
			},
			onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled
					dispatch(
						api.util.updateQueryData('getRecordings', undefined, draft => {
							const index = draft.findIndex(r => r.id === data.id)
							if (index !== -1) Object.assign(draft[index], data)
						})
					)
				} catch (error) {
					logger.error('getRecording', 'Failed to fetch recording', error)
				}
			},
		}),
		startRecording: builder.mutation({
			query: ({ sessionId, fps, rec_source }) => ({
				url: `sessions/${sessionId}/recordings`,
				method: 'POST',
				body: {
					fps,
					name: `OY_${new Date().toISOString().replace(/[:-]/g, '')}`,
					...(rec_source && { rec_source: true }),
				},
			}),
		}),
		stopRecording: builder.mutation({
			query: ({ sessionId, recordingId }) => ({
				url: `sessions/${sessionId}/recordings/${recordingId}/stop`,
				method: 'POST',
			}),
			onQueryStarted: async ({ sessionId, recordingId }, { dispatch, queryFulfilled }) => {
				logger.info('stopRecording', `Stopping recording ${recordingId} for session ${sessionId}`)
				// Before the request: `recording_ended` can arrive over Ably ahead of the HTTP response.
				expectMedia('recordings', recordingId)

				try {
					await queryFulfilled
					logger.info('stopRecording', `Recording ${recordingId} stop request sent successfully`)
					dispatch(
						api.util.updateQueryData('getRecordings', undefined, d => {
							const rec = d.find(r => r.id === recordingId)
							if (rec) {
								rec.status = RECORDING_STATUS.STOP_REQUESTED
								// serverNow(): `end` is compared against serverNow() by the merge-progress bands and sits alongside the server-issued `start`. See ~/lib/serverClock.
								rec.end = serverNow()
							}
						})
					)
				} catch (error) {
					logger.error('stopRecording', 'Failed to send stop recording request', error)
				}
			},
			transformResponse: res => res.recording,
		}),
		patchRecording: builder.mutation({
			query: ({ sessionId, recordingId, name }) => ({
				url: `sessions/${sessionId}/recordings/${recordingId}`,
				method: 'PATCH',
				body: { name },
			}),
			onQueryStarted({ recordingId, name }, { dispatch, queryFulfilled }) {
				const patch = dispatch(
					api.util.updateQueryData('getRecordings', undefined, d => {
						const rec = d.find(r => r.id === recordingId)
						if (rec) rec.name = name
					})
				)
				queryFulfilled.catch(patch.undo)
			},
		}),
		deleteRecording: builder.mutation({
			query: ({ sessionId, recordingId }) => ({
				url: `sessions/${sessionId}/recordings/${recordingId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (result, error, { recordingId }) => [
				{ type: RECORDING, id: recordingId },
				LIST_RECORDINGS,
			],
			onQueryStarted({ recordingId }, { dispatch, queryFulfilled }) {
				logger.info('deleteRecording', `Deleting recording ${recordingId}`)
				const deleteResult = dispatch(
					api.util.updateQueryData('getRecordings', undefined, d => {
						const i = d.findIndex(r => r.id === recordingId)
						if (i !== -1) d.splice(i, 1)
					})
				)
				queryFulfilled
					.then(() => {
						logger.info('deleteRecording', `Recording ${recordingId} deleted successfully`)
					})
					.catch(error => {
						logger.error('deleteRecording', `Failed to delete recording ${recordingId}`, error)
						deleteResult.undo()
					})
			},
		}),
		deleteRecordings: builder.mutation({
			query: ids => ({
				url: 'recordings',
				method: 'DELETE',
				body: { ids },
			}),
			onQueryStarted(ids, { dispatch, queryFulfilled }) {
				const idsSet = new Set(ids)
				const deleteResult = dispatch(
					api.util.updateQueryData('getRecordings', undefined, d => {
						return d.filter(r => !idsSet.has(r.id))
					})
				)
				queryFulfilled
					.then(() => {
						logger.info('deleteRecordings', `Deleted ${ids.length} recordings`)
					})
					.catch(error => {
						logger.error('deleteRecordings', `Failed to delete recordings`, error)
						deleteResult.undo()
					})
			},
		}),
		// images
		getImages: builder.query({
			query: () => 'images',
			transformResponse: res => res.images.map(transformImage).sort((a, b) => b.start - a.start),
		}),
		deleteImages: builder.mutation({
			query: ids => ({
				url: 'images',
				method: 'DELETE',
				body: { ids },
			}),
			onQueryStarted(ids, { dispatch, queryFulfilled }) {
				const idsSet = new Set(ids)
				const deleteResult = dispatch(
					api.util.updateQueryData('getImages', undefined, d => d.filter(i => !idsSet.has(i.id)))
				)
				queryFulfilled.catch(error => {
					logger.error('deleteImages', 'Failed to delete images', error)
					deleteResult.undo()
				})
			},
		}),
		publishRecording: builder.mutation({
			query: ({ sessionId, recordingId }) => ({
				url: `sessions/${sessionId}/recordings/${recordingId}/publish`,
				method: 'POST',
			}),
			onQueryStarted: async ({ recordingId }, { dispatch, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled
					const rec = data?.recording
					if (rec) {
						dispatch(
							api.util.updateQueryData('getRecordings', undefined, d => {
								const existing = d.find(r => r.id === recordingId)
								if (existing) {
									existing.isPublic = true
									existing.publishedAt = rec.publishedAt
									// publicUrl is a second, unsigned copy — keep downloadUrl (the signed
									// original) intact so saving/downloading still works after publishing.
									existing.publicUrl = rec.publicUrl
								}
							})
						)
					}
				} catch (error) {
					logger.error('publishRecording', `Failed to publish recording ${recordingId}`, error)
				}
			},
		}),
		unpublishRecording: builder.mutation({
			query: ({ sessionId, recordingId }) => ({
				url: `sessions/${sessionId}/recordings/${recordingId}/unpublish`,
				method: 'POST',
			}),
			onQueryStarted: async ({ recordingId }, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled
					dispatch(
						api.util.updateQueryData('getRecordings', undefined, d => {
							const existing = d.find(r => r.id === recordingId)
							if (existing) {
								existing.isPublic = false
								existing.publishedAt = null
								existing.publicUrl = null
							}
						})
					)
				} catch (error) {
					logger.error('unpublishRecording', `Failed to unpublish recording ${recordingId}`, error)
				}
			},
		}),
		//* Image Assets <-- NEW
		/*
		GET /assets - will return all the users assets info
		GET /assets/:id - will return a specific asset info
		POST /assets { file, metadata: {...}, thumbnail <boolean> } - will create and return asset's info. metadata is optional and can be whatever you want. thumbnail is optional, if it exists a thumbnail will also be created.
		PATCH /assets/:id { name, metadata } - allows updating asset's filename and metadata
		DELETE /assets/:id - deletes an asset
		 */
		getAssets: builder.query({
			query: () => 'assets',
			transformResponse: res => {
				if (!res || !res.assets) return EMPTY_ARRAY
				return res.assets
			},
			providesTags: result =>
				result ? [...result.map(({ id }) => ({ type: ASSET, id })), LIST_ASSETS] : [LIST_ASSETS],
		}),
		getAsset: builder.query({
			query: id => `assets/${id}`,
			transformResponse: res => {
				if (!res || !res.asset) return null
				return res.asset
			},
			providesTags: (result, error, id) => [{ type: ASSET, id }],
		}),
		addAsset: builder.mutation({
			query: ({ formData }) => ({
				url: 'assets',
				method: 'POST',
				body: formData,
			}),
			transformResponse: res => {
				if (!res || !res.asset) return null
				return res.asset
			},
			async onQueryStarted({ file }, { dispatch, queryFulfilled }) {
				const tempId = `temp_asset_${uuid()}`
				const blobUrl = URL.createObjectURL(file)
				const optimisticAsset = {
					id: tempId,
					name: file.name,
					size: file.size,
					mimeType: file.type,
					createdAt: new Date().toISOString(),
					thumbnailUrl: blobUrl,
					mediaUrl: blobUrl,
				}
				const patch = dispatch(
					api.util.updateQueryData('getAssets', undefined, d => {
						d.unshift(optimisticAsset)
					})
				)
				try {
					const { data: asset } = await queryFulfilled
					dispatch(
						api.util.updateQueryData('getAssets', undefined, d => {
							const idx = d.findIndex(a => a.id === tempId)
							if (idx !== -1) d[idx] = asset
						})
					)
					URL.revokeObjectURL(blobUrl)
				} catch {
					patch.undo()
					URL.revokeObjectURL(blobUrl)
				}
			},
		}),
		updateAsset: builder.mutation({
			query: ({ id, ...body }) => ({
				url: `assets/${id}`,
				method: 'PATCH',
				body,
			}),
			transformResponse: res => {
				if (!res || !res.asset) return null
				return res.asset
			},
			async onQueryStarted({ id, ...body }, { dispatch, queryFulfilled }) {
				logger.info('updateAsset', `Updating asset ${id}`)
				// Optimistically update the getAssets cache
				const patchResult = dispatch(
					api.util.updateQueryData('getAssets', undefined, d => {
						const asset = d.find(a => a.id === id)
						if (asset) {
							Object.assign(asset, body)
						} else {
							logger.warn('updateAsset', `Asset ${id} not found in cache`)
						}
					})
				)
				try {
					await queryFulfilled
					logger.info('updateAsset', `Asset ${id} updated successfully`)
				} catch (error) {
					logger.error('updateAsset', `Failed to update asset ${id}`, error)
					patchResult.undo()
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: ASSET, id }],
		}),
		deleteAssets: builder.mutation({
			query: ({ ids, force }) => ({
				url: 'assets',
				method: 'DELETE',
				body: { ids, force },
			}),
			async onQueryStarted({ ids, force }, { dispatch, getState, queryFulfilled }) {
				logger.info('deleteAssets', `Deleting assets ${ids.join(', ')}`)
				let patchResult
				if (force) {
					patchResult = dispatch(
						api.util.updateQueryData('getAssets', undefined, d => {
							const idSet = new Set(ids)
							return d.filter(a => !idSet.has(a.id))
						})
					)
				}
				try {
					const { data } = await queryFulfilled
					if (!force) {
						dispatch(
							api.util.updateQueryData('getAssets', undefined, d => {
								const idSet = new Set(ids)
								return d.filter(a => !idSet.has(a.id))
							})
						)
					}
					if (force) {
						if (data.affectedProjectIds?.length) {
							for (const pid of data.affectedProjectIds) {
								dispatch(api.util.invalidateTags([{ type: PROJECT, id: pid }]))
							}
						}
						const objects = getState().live.objects
						const assetSet = new Set(ids.map(id => `asset:${id}`))
						const toDelete = Object.keys(objects).filter(oid => assetSet.has(objects[oid]?.source))
						if (toDelete.length) {
							dispatch(deleteObjects(toDelete))
						}
					}
					// The style image is "{assetId}:{name}": a dangling one would be saved back into presets and pins.
					const style = morphToPlain(getState().live.ip?.asset_id)
					if (style && ids.includes(style.split(':')[0])) dispatch(setLiveProperty(['ip.asset_id', '']))
					logger.info('deleteAssets', `Assets deleted successfully`)
				} catch (error) {
					logger.error('deleteAssets', `Failed to delete assets`, error)
					patchResult?.undo()
				}
			},
		}),
		// feedback
		sendFeedback: builder.mutation({
			query: body => ({
				url: 'me/reachout',
				method: 'POST',
				body,
			}),
		}),
		deleteAccount: builder.mutation({
			query: () => ({
				url: 'user/me',
				method: 'DELETE',
			}),
		}),
		// maps
		getMaps: builder.query({
			query: () => 'maps',
			transformResponse: res => (res?.maps ? res.maps.map(transformMap) : EMPTY_ARRAY),
			providesTags: result =>
				result ? [...result.map(({ id }) => ({ type: MAP, id })), LIST_MAPS] : [LIST_MAPS],
		}),
		addMap: builder.mutation({
			query: ({ name, data, thumbnail }) => ({
				url: 'maps',
				method: 'POST',
				body: mapBody({ name, data: data ?? {}, thumbnail }),
			}),
			transformResponse: res => transformMap(res?.map),
			invalidatesTags: [LIST_MAPS],
		}),
		updateMap: builder.mutation({
			query: ({ id, name, data, thumbnail }) => ({
				url: `maps/${id}`,
				method: 'PATCH',
				body: mapBody({ name, data, thumbnail }),
			}),
			transformResponse: res => transformMap(res?.map),
			invalidatesTags: (result, error, { id }) => [{ type: MAP, id }],
		}),
		deleteMap: builder.mutation({
			query: id => ({ url: `maps/${id}`, method: 'DELETE' }),
			invalidatesTags: [LIST_MAPS],
		}),
		// config
		getConfig: builder.query({
			query: () => 'me/config',
		}),
	}),
})

export { TAGS }

export const {
	// * user
	useGetUserQuery,
	// * projects
	useGetProjectsQuery,
	useGetProjectQuery,
	useAddProjectMutation,
	// useAddProjectsMutation,
	useUpdateProjectMutation,
	useDeleteProjectMutation,
	// useDeleteProjectsMutation,
	// * presets useGetPresetsQuery,
	useGetPresetQuery,
	useLazyGetPresetQuery,
	useAddPresetMutation,
	useAddPresetsMutation,
	useUpdatePresetMutation,
	// useDeletePresetMutation,
	useDeletePresetsMutation,
	// * templates
	useGetTemplatesQuery,
	useGetTemplateQuery,
	useAddTemplateMutation,
	useUpdateTemplateMutation,
	useDeleteTemplateMutation,
	useSaveTemplateAsMutation,
	useSyncTemplateFromProjectMutation,
	useGetExamplePresetsQuery,
	// * sessions
	useGetSessionsQuery,
	useGetActiveSessionQuery,
	useCreateSessionMutation,
	useStopSessionMutation,
	// * credits
	useGetCreditsQuery,
	useGetCreditPackagesQuery,
	useCreatePaymentIntentMutation,
	// * recordings
	useGetRecordingsQuery,
	useStartRecordingMutation,
	useStopRecordingMutation,
	usePatchRecordingMutation,
	useDeleteRecordingMutation,
	useDeleteRecordingsMutation,
	usePublishRecordingMutation,
	useUnpublishRecordingMutation,
	// * images
	useGetImagesQuery,
	useDeleteImagesMutation,
	// * feedback
	useSendFeedbackMutation,
	// * account
	useDeleteAccountMutation,
	// Add lazy query hook export
	useLazyGetProjectQuery,
	// * assets <-- New
	useGetAssetsQuery,
	useGetAssetQuery,
	useAddAssetMutation,
	useUpdateAssetMutation,
	useDeleteAssetsMutation,
	// * maps
	useGetMapsQuery,
	useAddMapMutation,
	useUpdateMapMutation,
	useDeleteMapMutation,
} = api

// Only expose API globally in development
if (IS_DEV) {
	window.api = api
}

export default api
